import { useState, useEffect, useRef } from 'react'
import { supabase } from '../../../lib/supabase'
import GridLayout from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import type { ComponentType } from '../../../types/builder.types'
import { ComponentPreview } from './ComponentPreview'

interface Component {
  id: string
  name: string
  type: ComponentType
  is_published: boolean,
  configuration: any
}

interface DashboardComponent {
  id: string
  dashboard_id: string
  component_id: string
  position?: number
  grid_column: number
  grid_row: number
  grid_x: number
  grid_y: number
  component?: Component
}

interface Dashboard {
  id: string
  name: string
  slug: string
}

interface DashboardEditorProps {
  dashboardId: string
  onBack: () => void
}

export function DashboardEditor({ dashboardId, onBack }: DashboardEditorProps) {
  const [dashboard, setDashboard] = useState<Dashboard | null>(null)
  const [availableComponents, setAvailableComponents] = useState<Component[]>([])
  const [dashboardComponents, setDashboardComponents] = useState<DashboardComponent[]>([])
  const [currentLayout, setCurrentLayout] = useState<any[]>([])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const isInitialMount = useRef(true)

  useEffect(() => {
    loadData()
  }, [dashboardId])

  const loadData = async () => {
    try {
      setLoading(true)

      // Load dashboard info
      // @ts-ignore
      const { data: dashData, error: dashError } = await supabase
        .from('dashboards')
        .select('id, name, slug')
        .eq('id', dashboardId)
        .single()

      if (dashError) throw dashError
      setDashboard(dashData)

      // Load available components (published only)
      // @ts-ignore
      const { data: comps, error: compsError } = await supabase
        .from('components')
        .select('id, name, type, is_published')
        .eq('is_published', true)
        .order('created_at', { ascending: false })

      if (compsError) throw compsError
      setAvailableComponents(comps || [])

      // Load current dashboard components with grid positions
      // @ts-ignore
      const { data: dashComps, error: dashCompsError } = await supabase
        .from('dashboard_components')
        .select(`
          id,
          dashboard_id,
          component_id,
          position,
          grid_column,
          grid_row,
          grid_x,
          grid_y,
          components (id, name, type, configuration)
        `)
        .eq('dashboard_id', dashboardId)
        .order('grid_y', { ascending: true })
        .order('grid_x', { ascending: true })
        //.order('position', { ascending: true })

      if (dashCompsError) throw dashCompsError

      // Process data - flatten component relation
      const processedComps = (dashComps || []).map((dc: any) => ({
        id: dc.id,
        dashboard_id: dc.dashboard_id,
        component_id: dc.component_id,
        //position: dc.position,
        grid_column: dc.grid_column || 4,
        grid_row: dc.grid_row || 2,
        grid_x: dc.grid_x || 0,
        grid_y: dc.grid_y || 0,
        component: dc.components,
        
      }))

      setDashboardComponents(processedComps)

      // Build initial layout from saved positions
      const initialLayout = processedComps.map((dc) => ({
        i: dc.id,
        x: dc.grid_x ?? 0,
        y: dc.grid_y ?? 0,
        w: dc.grid_column ?? 4,
        h: dc.grid_row ?? 2,
      }))

      //codigo antiguo
      /*const initialLayout = processedComps.map((dc: DashboardComponent, index: number) => ({
        i: dc.id,
        x: dc.grid_x || 0,
        y: dc.grid_y || (index * 2),
        w: dc.grid_column || 4,
        h: dc.grid_row || 2,
      }))*/

      setCurrentLayout(initialLayout)
      setHasUnsavedChanges(false)

      // Mark that initial mount is complete
      if (isInitialMount.current) {
        isInitialMount.current = false
      }
    } catch (err) {
      console.error('Error cargando datos:', err)
      alert('Error al cargar los datos del dashboard')
    } finally {
      setLoading(false)
    }
  }

  const handleAddComponent = async (componentId: string) => {
    try {
      // Check if already added
      if (dashboardComponents.some(dc => dc.component_id === componentId)) {
        alert('Este componente ya está en el dashboard')
        return
      }

      

      // Find next position (bottom of grid)
      const maxY = currentLayout.length > 0
        ? Math.max(...currentLayout.map(item => item.y + item.h))
        : 0
      

      // Insert into DB
      const query = supabase.from('dashboard_components')
      const { error } = await query
        // @ts-ignore
        .insert({
          dashboard_id: dashboardId,
          component_id: componentId,
          //position: nextPosition,
          grid_column: 4, // Default: 1/3 of 12 columns
          grid_row: 2,
          grid_x: 0,
          grid_y: maxY,
        })
        .select()
        .single()

      if (error) throw error

      // Reload to get component data
      await loadData()
    } catch (err) {
      console.error('Error agregando componente:', err)
      alert('Error al agregar el componente')
    }
  }

  const handleRemoveComponent = async (dashboardComponentId: string) => {
    if (!confirm('¿Quitar este componente del dashboard?')) return

    try {
      // @ts-ignore
      const { error } = await supabase
        .from('dashboard_components')
        .delete()
        .eq('id', dashboardComponentId)

      if (error) throw error

      await loadData()
    } catch (err) {
      console.error('Error quitando componente:', err)
      alert('Error al quitar el componente')
    }
  }

  const handleLayoutChange = (newLayout: any[]) => {
    // Skip during initial mount
    if (isInitialMount.current || dashboardComponents.length === 0) return

    // Update current layout and mark as having changes
    
    setCurrentLayout(newLayout)
    setHasUnsavedChanges(true)
  }

  const handleSaveLayout = async () => {
    if (!hasUnsavedChanges) return

    try {
      setSaving(true)

      // Update positions and dimensions for all components
      const updates = currentLayout.map((item) => {
        
        return supabase
          .from('dashboard_components')
          // @ts-ignore
          .update({
            //position: index,
            grid_x: item.x,
            grid_y: item.y,
            grid_column: item.w,
            grid_row: item.h,
          })
          .eq('id', item.i)
      })

      await Promise.all(updates)

      // Update local state
      setDashboardComponents(prev =>
        prev.map(dc => {
          const layoutItem = currentLayout.find(item => item.i === dc.id)
          if (!layoutItem) return dc

          return {
            ...dc,
            //position: currentLayout.indexOf(layoutItem),
            grid_x: layoutItem.x,
            grid_y: layoutItem.y,
            grid_column: layoutItem.w,
            grid_row: layoutItem.h,
          }
        })
      )

      setHasUnsavedChanges(false)

      // Show success feedback
      const btn = document.getElementById('save-btn')
      if (btn) {
        const originalText = btn.innerHTML
        btn.innerHTML = '✓ Guardado'
        setTimeout(() => {
          btn.innerHTML = originalText
        }, 2000)
      }
    } catch (err) {
      console.error('Error guardando layout:', err)
      alert('Error al guardar cambios')
    } finally {
      setSaving(false)
    }
  }

  const getComponentIcon = (type: ComponentType) => {
    switch (type) {
      // Componentes originales
      case 'kpi': return '📊'
      case 'line-chart': return '📈'
      case 'bar-chart': return '📊'
      case 'table': return '📋'
      // Fase 1: Críticos
      case 'map': return '🗺️'
      case 'pie-chart': return '🥧'
      case 'gauge': return '🎯'
      // Fase 2: Muy útiles
      case 'area-chart': return '📈'
      case 'stacked-bar': return '📊'
      case 'scatter-plot': return '🔵'
      // Fase 3: Nice to have
      case 'heatmap': return '🔥'
      case 'treemap': return '🌳'
      case 'radar-chart': return '🎯'
      case 'radial-bar': return '🔵'
      default: return '❓'
    }
  }

  const getComponentTypeLabel = (type: ComponentType) => {
    switch (type) {
      // Componentes originales
      case 'kpi': return 'KPI'
      case 'line-chart': return 'Gráfico Línea'
      case 'bar-chart': return 'Gráfico Barras'
      case 'table': return 'Tabla'
      // Fase 1: Críticos
      case 'map': return 'Mapa Interactivo'
      case 'pie-chart': return 'Gráfico Circular'
      case 'gauge': return 'Medidor'
      // Fase 2: Muy útiles
      case 'area-chart': return 'Gráfico de Área'
      case 'stacked-bar': return 'Barras Apiladas'
      case 'scatter-plot': return 'Dispersión'
      // Fase 3: Nice to have
      case 'heatmap': return 'Mapa de Calor'
      case 'treemap': return 'Mapa de Árbol'
      case 'radar-chart': return 'Gráfico Radial'
      case 'radial-bar': return 'Barras Radiales'
      default: return type
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando editor...</p>
        </div>
      </div>
    )
  }

  if (!dashboard) {
    return (
      <div className="text-center py-16">
        <p className="text-red-600">Dashboard no encontrado</p>
        <button onClick={onBack} className="mt-4 px-4 py-2 bg-gray-600 text-white rounded">
          Volver
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-full mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="px-4 py-2 border-2 border-gray-300 rounded-lg font-medium hover:bg-gray-50"
          >
            ← Volver
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Editando: {dashboard.name}
            </h1>
            <p className="text-sm text-gray-500">
              Arrastra y redimensiona componentes. {hasUnsavedChanges && <span className="text-orange-600 font-medium">Hay cambios sin guardar.</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {hasUnsavedChanges && (
            <button
              id="save-btn"
              onClick={handleSaveLayout}
              disabled={saving}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                saving
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700 shadow-lg'
              }`}
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Guardando...
                </span>
              ) : (
                '💾 Guardar Cambios'
              )}
            </button>
          )}
          <a
            href={`/?dashboard=${dashboard.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
          >
            👁️ Vista Previa
          </a>
        </div>
      </div>

      {/* Main Layout: Vertical Stack */}
      <div className="flex flex-col gap-6">
        {/* Componentes Disponibles - Horizontal con scroll */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-semibold mb-3 text-sm text-gray-700">Componentes Disponibles</h3>

          {availableComponents.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              No hay componentes publicados
            </p>
          ) : (
            <div className="overflow-x-auto -mx-2 px-2">
              <div className="flex gap-3 pb-2">
                {availableComponents.map(comp => {
                  const isAdded = dashboardComponents.some(dc => dc.component_id === comp.id)
                  return (
                    <div
                      key={comp.id}
                      className={`flex-shrink-0 w-48 border rounded-lg p-2.5 transition-all ${
                        isAdded
                          ? 'bg-gray-50 border-gray-300'
                          : 'bg-white border-gray-300 hover:border-green-400 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">{getComponentIcon(comp.type)}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-xs truncate leading-tight">{comp.name}</p>
                          <p className="text-[10px] text-gray-500 leading-tight">{getComponentTypeLabel(comp.type)}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleAddComponent(comp.id)}
                        disabled={isAdded}
                        className={`w-full py-1 rounded text-xs font-medium transition-colors ${
                          isAdded
                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                      >
                        {isAdded ? '✓ Agregado' : '+ Agregar'}
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Grid Area - Full Width */}
        <div className="bg-white border rounded-lg p-6">
          <h3 className="font-semibold mb-4">Layout del Dashboard (12 columnas)</h3>

          {dashboardComponents.length === 0 ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
              <div className="text-6xl mb-4">📦</div>
              <p className="text-gray-600 mb-2">Dashboard vacío</p>
              <p className="text-sm text-gray-500">
                Agrega componentes desde la barra superior
              </p>
            </div>
          ) : (
              <div className="relative">
                {/* Guías visuales de columnas */}
                <div className="absolute inset-0 pointer-events-none z-0" style={{ width: 1200 }}>
                  <div className="grid grid-cols-12 h-full" style={{ gap: '16px' }}>
                    {Array.from({ length: 12 }).map((_, i) => (
                      <div key={i} className="border-l border-dashed border-gray-300 relative">
                        <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-medium text-gray-400 bg-white px-1">
                          {i + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <GridLayout
                  className="layout"
                  layout={currentLayout}
                  cols={12}
                  rowHeight={120}
                  width={1200}
                  onLayoutChange={handleLayoutChange}
                  onDragStop={handleLayoutChange}
                  onResizeStop={handleLayoutChange}
                  isDraggable={true}
                  isResizable={true}
                  compactType={null}
                  preventCollision={false}
                  margin={[16, 16]}
                >
                  {dashboardComponents.map(dc => (
                    <div
                      key={dc.id}
                      className="bg-white border-2 border-green-300 rounded-lg overflow-hidden cursor-move relative group shadow-sm hover:shadow-md transition-shadow"
                    >
                      {/* Remove button */}
                      <button
                            draggable={false}
                           onPointerDown={(e) => {
                              e.stopPropagation()
                            }}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleRemoveComponent(dc.id)
                            }}
                          className="
                            absolute -top-3 right-2
                            w-10 h-10
                            bg-red-600 text-white
                            rounded-full
                            border-2 border-white
                            shadow-lg
                            z-20
                            flex items-center justify-center
                            transition-all
                            hover:bg-red-700 hover:scale-105
                            opacity-100 lg:opacity-0 lg:group-hover:opacity-100
                          "
                          aria-label="Eliminar componente"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2.5}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>

                      {/* Header flotante con info del componente */}
                      <div className="absolute top-0 left-0 right-0 bg-white/95 backdrop-blur-sm px-3 py-1.5 text-xs z-10 border-b border-green-300">
                        <div className="flex justify-between items-center gap-2">
                          <span className="font-semibold truncate flex items-center gap-1.5">
                            <span className="text-base">{getComponentIcon(dc.component?.type as any)}</span>
                            <span className="truncate">{dc.component?.name}</span>
                          </span>
                          <span className="text-gray-600 font-mono text-[10px] whitespace-nowrap">
                            {dc.grid_column}×{dc.grid_row} ({dc.grid_column * 100}×{dc.grid_row * 120}px)
                          </span>
                        </div>
                      </div>

                      {/* Preview real del componente */}
                      <div className="pt-8 h-full w-full overflow-hidden">
                        <ComponentPreview
                          component={{
                            id: dc.component?.id || '',
                            name: dc.component?.name || '',
                            type: dc.component?.type as ComponentType,
                            //configuration: dc.component?.configuration,
                          }}
                        />
                      </div>

                      {/* Drag handle indicator */}
                      <div className="absolute bottom-2 right-2 text-gray-400 text-base opacity-50 group-hover:opacity-100 transition-opacity pointer-events-none">
                        ⋮⋮
                      </div>
                    </div>
                  ))}
                </GridLayout>
              </div>
            )}
          </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-2">💡 Instrucciones</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>Agregar:</strong> Usa la barra de componentes en la parte superior</li>
            <li>• <strong>Mover:</strong> Arrastra los componentes a la posición deseada</li>
            <li>• <strong>Redimensionar:</strong> Arrastra desde la esquina inferior derecha</li>
            <li>• <strong>Quitar:</strong> Haz hover y click en la "✕"</li>
            <li>• <strong>Guardar:</strong> Click en "Guardar Cambios" cuando termines de organizar</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
